import { create } from "zustand";
import { useStoreHome } from "../Home/useStoreHome";
import { abrirCajaApi, cerrarCajaApi, obtenerResumenCaja, registrarMovimientoCaja, resolverCajaApi } from "../../servicios/cajasApi";
import { CAJA_SESION_STORAGE, esErrorSesionCaja, normalizarResumenCaja, respuestaCaja, sesionAbierta, sesionCajaDeRespuesta } from "../../servicios/cajas";
import { obtenerContextoSesion } from "../../servicios/contextoSesion";

const contexto = () => obtenerContextoSesion({ EmpresaId: useStoreHome.getState().EmpresaId });

let resolviendo = null;
let abriendo = false;
let cerrando = false;
let moviendo = false;

export const useStoreCajas = create((set, get) => ({
  sesionActiva: null, cajaSesionId: 0, estado: "SinCaja", resumen: null,
  cargando: false, error: "", ultimaActualizacion: null, aperturaRequerida: false, cerrando: false, movimientoEnCurso: false,
  limpiarSesion: () => { localStorage.removeItem(CAJA_SESION_STORAGE); set({ sesionActiva: null, cajaSesionId: 0, estado: "SinCaja", resumen: null, aperturaRequerida: false }); },
  resolverCaja: async () => {
    if (resolviendo) return resolviendo;
    resolviendo = (async () => {
      const ctx = contexto();
      if (!ctx.EmpresaId || !ctx.operadorId || !ctx.pcId) {
        const error = "No se pudo resolver el contexto de empresa, operador o PC.";
        set({ error, aperturaRequerida: false, cargando: false }); return null;
      }
      set({ cargando: true, error: "" });
      const normal = respuestaCaja(await resolverCajaApi(ctx));
      const sesion = normal.error ? null : sesionCajaDeRespuesta(normal.datos);
      if (sesionAbierta(sesion)) {
        const id = Number(sesion.id ?? sesion.Id);
        localStorage.setItem(CAJA_SESION_STORAGE, String(id));
        set({ sesionActiva: sesion, cajaSesionId: id, estado: "Abierta", aperturaRequerida: false, error: "", ultimaActualizacion: new Date().toISOString() });
        return sesion;
      }
      localStorage.removeItem(CAJA_SESION_STORAGE);
      set({ sesionActiva: null, cajaSesionId: 0, estado: "SinCaja", resumen: null, error: normal.error, aperturaRequerida: false, ultimaActualizacion: new Date().toISOString() });
      return null;
    })().finally(() => { resolviendo = null; set({ cargando: false }); });
    return resolviendo;
  },
  abrirCaja: async ({ cajaId, saldoInicial, observacion }) => {
    if (abriendo) return false; abriendo = true; set({ cargando: true, error: "" });
    try {
      const ctx = contexto();
      const normal = respuestaCaja(await abrirCajaApi({ EmpresaId: ctx.EmpresaId, cajaId: Number(cajaId), operadorId: ctx.operadorId, pcId: ctx.pcId, saldoInicial: Number(saldoInicial), observacion: observacion || "" }));
      const sesion = normal.error ? null : sesionCajaDeRespuesta(normal.datos);
      if (normal.error || !sesionAbierta(sesion)) { set({ error: normal.error || "El servidor no devolvio una sesion abierta." }); return false; }
      const id = Number(sesion.id ?? sesion.Id); localStorage.setItem(CAJA_SESION_STORAGE, String(id));
      set({ sesionActiva: sesion, cajaSesionId: id, estado: "Abierta", aperturaRequerida: false, error: "", ultimaActualizacion: new Date().toISOString() }); return true;
    } finally { abriendo = false; set({ cargando: false }); }
  },
  actualizarResumen: async (cajaSesionId = get().cajaSesionId) => {
    if (!cajaSesionId) return null; set({ cargando: true, error: "" });
    try {
      const normal = respuestaCaja(await obtenerResumenCaja({ EmpresaId: contexto().EmpresaId, cajaSesionId: Number(cajaSesionId) }));
      if (normal.error) { if (esErrorSesionCaja(normal.error)) get().limpiarSesion(); set({ error: normal.error }); return null; }
      const resumen = normalizarResumenCaja(normal.datos); const sesion = resumen.sesion;
      set({ resumen, ...(sesionAbierta(sesion) ? { sesionActiva: sesion, estado: "Abierta" } : {}), ultimaActualizacion: new Date().toISOString() }); return resumen;
    } finally { set({ cargando: false }); }
  },
  registrarMovimiento: async (movimiento) => {
    if (moviendo || get().cerrando || !sesionAbierta(get().sesionActiva)) return false; moviendo = true; set({ movimientoEnCurso: true, error: "" });
    try {
      const ctx = contexto(); const normal = respuestaCaja(await registrarMovimientoCaja({
        EmpresaId: ctx.EmpresaId,
        cajaSesionId: get().cajaSesionId,
        tipo: movimiento.tipo,
        importe: Number(movimiento.importe),
        concepto: movimiento.concepto,
        claveIdempotencia: movimiento.claveIdempotencia,
        operadorId: ctx.operadorId,
        pcId: ctx.pcId,
      }));
      if (normal.error) { if (esErrorSesionCaja(normal.error)) await get().resolverCaja(); set({ error: normal.error }); return false; }
      await get().actualizarResumen(); return true;
    } finally { moviendo = false; set({ movimientoEnCurso: false }); }
  },
  cerrarCaja: async ({ saldoDeclarado, observacion }) => {
    if (cerrando || !sesionAbierta(get().sesionActiva)) return null; cerrando = true; set({ cerrando: true, error: "" });
    try {
      const resumen = await get().actualizarResumen();
      if (!resumen || !sesionAbierta(get().sesionActiva)) return null;
      const ctx = contexto();
      const normal = respuestaCaja(await cerrarCajaApi({ EmpresaId: ctx.EmpresaId, cajaSesionId: get().cajaSesionId, operadorId: ctx.operadorId, pcId: ctx.pcId, saldoDeclarado: Number(saldoDeclarado), observacion: observacion || "" }));
      if (normal.error) { set({ error: normal.error }); return null; }
      const sesionCerrada = sesionCajaDeRespuesta(normal.datos); get().limpiarSesion(); set({ ultimaSesionCerrada: sesionCerrada, aperturaRequerida: false }); return sesionCerrada;
    } finally { cerrando = false; set({ cerrando: false }); }
  },
}));
